import React, { useEffect, useMemo, useState } from "react";
import AdjustableAmortizationTable from "../components/AdjustableAmortizationTable";
import { Link } from "react-router-dom";
import {
  fmtMoney,
  validateAmortizationInputs,
  buildAdjustableAmortizationSchedule,
} from "../utils/amortizationHelpers";

/**
 * ***************************************************************
 * ADJUSTABLE AMORTIZATION PAGE
 * ***************************************************************
 *
 * Purpose:
 *   Displays an amortization schedule where the user can modify
 *   the number of days for each payment period.
 *
 * Key Features:
 *   - User enters loan inputs (price, down payment, years, APR, etc.)
 *   - Days column in the table is editable
 *   - Payment column in the table is editable
 *   - Updating a row recalculates the entire amortization schedule
 *   - Input values at the top of this page persist in localStorage
 *
 * State Variables:
 *   price, downPayment, years, apr, daysToFirstPayment
 *     -> user inputs stored as strings
 *
 *   customDays
 *     -> array storing user-edited days for each row
 *
 *   customPayments
 *     -> array storing user-edited payment amounts for each row
 *
 * ***************************************************************
 */

function AdjustableAmortizationPage() {
  /**
   * -------------------------------------------------------------
   * INPUT STATE
   * -------------------------------------------------------------
   * Stores raw user input values as strings.
   *
   * Each value is initialized from localStorage so the top input
   * fields can persist after a browser refresh.
   */
  const [price, setPrice] = useState(
    () => localStorage.getItem("adjustablePrice") || ""
  );

  const [downPayment, setDownPayment] = useState(
    () => localStorage.getItem("adjustableDownPayment") || ""
  );

  const [years, setYears] = useState(
    () => localStorage.getItem("adjustableYears") || ""
  );

  const [apr, setApr] = useState(
    () => localStorage.getItem("adjustableApr") || ""
  );

  const [daysToFirstPayment, setDaysToFirstPayment] = useState(
    () => localStorage.getItem("adjustableDaysToFirstPayment") || ""
  );

  /**
   * -------------------------------------------------------------
   * CUSTOM TABLE EDIT STATE
   * -------------------------------------------------------------
   * These arrays remain unchanged from the current version.
   *
   * customDays:
   *   Stores edited day values by row index.
   *
   * customPayments:
   *   Stores edited payment values by row index.
   *
   * Per request, these are not being changed for persistence.
   */
  const [customDays, setCustomDays] = useState([]);
  const [customPayments, setCustomPayments] = useState([]);

  /**
   * -------------------------------------------------------------
   * PERSIST PRICE
   * -------------------------------------------------------------
   * Runs every time price changes.
   * Saves the current price field value into localStorage.
   */
  useEffect(() => {
    localStorage.setItem("adjustablePrice", price);
  }, [price]);

  /**
   * -------------------------------------------------------------
   * PERSIST DOWN PAYMENT
   * -------------------------------------------------------------
   * Runs every time downPayment changes.
   * Saves the current down payment field value into localStorage.
   */
  useEffect(() => {
    localStorage.setItem("adjustableDownPayment", downPayment);
  }, [downPayment]);

  /**
   * -------------------------------------------------------------
   * PERSIST YEARS
   * -------------------------------------------------------------
   * Runs every time years changes.
   * Saves the current years field value into localStorage.
   */
  useEffect(() => {
    localStorage.setItem("adjustableYears", years);
  }, [years]);

  /**
   * -------------------------------------------------------------
   * PERSIST APR
   * -------------------------------------------------------------
   * Runs every time apr changes.
   * Saves the current APR field value into localStorage.
   */
  useEffect(() => {
    localStorage.setItem("adjustableApr", apr);
  }, [apr]);

  /**
   * -------------------------------------------------------------
   * PERSIST DAYS TO FIRST PAYMENT
   * -------------------------------------------------------------
   * Runs every time daysToFirstPayment changes.
   * Saves the current first-payment-days value into localStorage.
   */
  useEffect(() => {
    localStorage.setItem(
      "adjustableDaysToFirstPayment",
      daysToFirstPayment
    );
  }, [daysToFirstPayment]);

  /**
   * -------------------------------------------------------------
   * PARSE USER INPUTS
   * -------------------------------------------------------------
   * Converts string inputs into numeric values.
   *
   * Number("") becomes 0, which is acceptable here because the
   * validation helper checks for invalid or missing input values.
   */
  const parsed = useMemo(() => {
    const p = Number(price);
    const d = Number(downPayment);
    const y = Number(years);
    const a = Number(apr);
    const days = Number(daysToFirstPayment);

    return {
      price: p,
      downPayment: d,
      years: y,
      apr: a,
      daysToFirstPayment: days,
      loanAmount: p - d,
    };
  }, [price, downPayment, years, apr, daysToFirstPayment]);

  /**
   * -------------------------------------------------------------
   * BUILD AMORTIZATION SCHEDULE
   * -------------------------------------------------------------
   * Recalculates the schedule whenever:
   *   - parsed input values change
   *   - customDays changes
   *   - customPayments changes
   *
   * If validation fails, returns an empty schedule and an error.
   */
  const amortization = useMemo(() => {
    const validationError = validateAmortizationInputs(parsed);

    if (validationError) {
      return {
        error: validationError,
        monthlyPayment: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        totalPaid: 0,
        rows: [],
      };
    }

    return buildAdjustableAmortizationSchedule({
      loanAmount: parsed.loanAmount,
      years: parsed.years,
      apr: parsed.apr,
      daysToFirstPayment: parsed.daysToFirstPayment,
      customDays,
      customPayments,
    });
  }, [parsed, customDays, customPayments]);

  /**
   * -------------------------------------------------------------
   * HANDLE EDITING OF DAYS COLUMN
   * -------------------------------------------------------------
   * Updates the customDays array at the specified row index.
   *
   * Parameters:
   *   rowIndex -> index of row being edited
   *   value    -> new days value from input field
   */
  const handleEditDays = (rowIndex, value) => {
    const numericValue = Number(value);

    setCustomDays((prev) => {
      const copy = [...prev];

      copy[rowIndex] =
        Number.isFinite(numericValue) && numericValue > 0
          ? numericValue
          : 1;

      return copy;
    });
  };

  /**
   * -------------------------------------------------------------
   * HANDLE EDITING OF PAYMENT COLUMN
   * -------------------------------------------------------------
   * Updates the customPayments array at the specified row index.
   *
   * Parameters:
   *   rowIndex -> index of row being edited
   *   value    -> new payment value from input field
   *
   * Payment is stored as a number rounded to two decimal places.
   */
  const handleEditPayment = (rowIndex, value) => {
    const numericValue = Number(value);

    setCustomPayments((prev) => {
      const copy = [...prev];

      copy[rowIndex] =
        Number.isFinite(numericValue) && numericValue >= 0
          ? Number(numericValue.toFixed(2))
          : 0;

      return copy;
    });
  };

  /**
   * -------------------------------------------------------------
   * RENDER PAGE
   * -------------------------------------------------------------
   */
  return (
    <>
    {/* ============================================================
                    RELATED TOOLS / INTERNAL LINKS
                    ============================================================ */}
        <article className="tool-content">
        <h2 >Related Tools</h2>
    
            <p >
              <Link to="/vsps">Vspeed</Link> |
              <Link to="/rollout">Ground Roll</Link> |
              <Link to="/navlog">NavLog</Link> |
              <Link to="/wtbals">Weight & Balance</Link> |
              <Link to="/density-altitude">Density Altitude</Link> |
              <Link to="/cw">Crosswind/Headwind</Link> |
              <Link to="/mnvr">Maneuvers</Link> |
              <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
              <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
              <Link to="/adjustable-amortization">Aircraft Loan</Link>
            </p>
        </article>
    <div className="amortization-page">
      <h2 style={{ fontSize: "30px"}}>Aircraft Loan Amortization Schedule</h2>
      <p style={{ fontSize: "20px"}}>
          Use this tool to obtain an approximate aircraft loan payment and
          amortization schedule based on the values entered.

          <br /><br />

          Note: Results are estimates only and may differ from actual loan
          terms, lender requirements, interest rates, fees, taxes, insurance
          costs, closing costs, and other financing expenses.
          <br /><br />

        </p>

      {/* ================= INPUT FORM ================= */}
      <div className="amortization-form">
        <div className="amortization-form-row">
          <label htmlFor="price">Price $</label>
          <input
            id="price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>

        <div className="amortization-form-row">
          <label htmlFor="downPayment">Down Pymt $</label>
          <input
            id="downPayment"
            type="number"
            value={downPayment}
            onChange={(e) => setDownPayment(e.target.value)}
          />
        </div>

        <div className="amortization-form-row">
          <label htmlFor="years">Years</label>
          <input
            id="years"
            type="number"
            value={years}
            onChange={(e) => setYears(e.target.value)}
          />
        </div>

        <div className="amortization-form-row">
          <label htmlFor="apr">APR (%)</label>
          <input
            id="apr"
            type="number"
            step="0.01"
            value={apr}
            onChange={(e) => setApr(e.target.value)}
          />
        </div>

        <div className="amortization-form-row">
          <label htmlFor="daysToFirstPayment">Days to First Payment</label>
          <input
            id="daysToFirstPayment"
            type="number"
            value={daysToFirstPayment}
            onChange={(e) => setDaysToFirstPayment(e.target.value)}
          />
        </div>
      </div>

      {/* ================= ERROR DISPLAY ================= */}
      {amortization.error ? (
        <div className="amortization-error">
          {amortization.error}
        </div>
      ) : (
        <>
          {/* ================= SUMMARY RESULTS ================= */}
          <div className="amortization-results">
            <div>
              <span className="label">Loan Amount:</span>
              <span className="value">
                {"$" + fmtMoney(parsed.loanAmount)}
              </span>
            </div>

            <div>
              <span className="label">Monthly Payment:</span>
              <span className="value">
                {"$" + fmtMoney(amortization.monthlyPayment)}
              </span>
            </div>

            <div>
              <span className="label">Total Interest:</span>
              <span className="value">
                {"$" + fmtMoney(amortization.totalInterest)}
              </span>
            </div>

            <div>
              <span className="label">Total Principal:</span>
              <span className="value">
                {"$" + fmtMoney(amortization.totalPrincipal)}
              </span>
            </div>

            <div>
              <span className="label">Total Paid:</span>
              <span className="value">
                {"$" + fmtMoney(amortization.totalPaid)}
              </span>
            </div>
          </div>

          {/* ================= TABLE ================= */}
          <AdjustableAmortizationTable
            rows={amortization.rows}
            onEditDays={handleEditDays}
            onEditPayment={handleEditPayment}
          />
        </>
      )}
    </div>
    {/* ============================================================
                    RELATED TOOLS / INTERNAL LINKS
                    ============================================================ */}
        <article className="tool-content">
        <h2 >Related Tools</h2>
    
            <p >
              <Link to="/vsps">Vspeed</Link> |
              <Link to="/rollout">Ground Roll</Link> |
              <Link to="/navlog">NavLog</Link> |
              <Link to="/wtbals">Weight & Balance</Link> |
              <Link to="/density-altitude">Density Altitude</Link> |
              <Link to="/cw">Crosswind/Headwind</Link> |
              <Link to="/mnvr">Maneuvers</Link> |
              <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
              <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
              <Link to="/adjustable-amortization">Aircraft Loan</Link>
            </p>
        </article>
    </>
  );
}

export default AdjustableAmortizationPage;